import { Core, Decorative } from "@faire/design-tokens";
import { PromoBannerType } from "@faire/retailer-visitor-shared/components/Notifications/PromoBanner/bannerOrder";
import { getBrand } from "@faire/retailer-visitor-shared/serialized-data/getBrand";
import {
  MediumFormatting,
  LinkFormatting,
} from "@faire/web--source/common/localization/formatting";
import { Typography } from "@faire/web--source/slate/Typography";
import { AlertBanner } from "@faire/web--source/ui/AlertBanner";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackBrandPreviewFairesArrangementLinkClick } from "@faire/web-api--source/events/brandPreview/click/fairesArrangementLink";
import { trackBrandPreviewReturnToBrandPortalLinkClick } from "@faire/web-api--source/events/brandPreview/click/returnToBrandPortalLink";
import { trackRetailerOnboardingTopPromoBannerView } from "@faire/web-api--source/events/retailerOnboarding/view/topPromoBanner";
import brandPortal from "@faire/web-api--source/routes/www/brand-portal";
import * as React from "react";
import { useEffect } from "react";

export const SEE_IT_IN_ACTION_LINK =
  "https://www.loom.com/share/310d410c13514dbeab63c3c26fb69e41?sid=b1050cf2-1b83-4320-9e55-73bf63704f0a";

export const BrandPreviewBanner: React.FC = () => {
  const brandPreviewContent = useBrandPreviewContent();

  useEffect(() => {
    // Special case banner that is not part of the normal banner rotation
    trackRetailerOnboardingTopPromoBannerView(PromoBannerType.BRAND_PREVIEW);
  }, []);

  return (
    <div data-test-id="brand-preview-banner">
      <AlertBanner
        color={Decorative.tint.neutral}
        message={
          <Typography color={Core.text.primary} align="center">
            {brandPreviewContent}
          </Typography>
        }
      />
    </div>
  );
};

const useBrandPreviewContent = () => {
  const isActiveBrand = !!getBrand()?.first_active_at;
  const isInactiveBrand = !getBrand()?.first_active_at;
  const isInactiveBrandWithProducts =
    isInactiveBrand && (getBrand()?.active_products_count ?? 0) > 0;

  switch (true) {
    case isActiveBrand:
      return (
        <StrictLocalMsg
          defaultMessage="Retailers will see a unique product arrangement based on their store type and browsing behavior. <LinkFormatting>See it in action</LinkFormatting>"
          description={{
            text: "Brand preview banner for active brands",
            img: "https://cdn.faire.com/fastly/9ad1519e88e3b20ce64f7632dee3ad6963b8951f13084033c5fbf0e59cb42378.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            LinkFormatting: LinkFormatting(SEE_IT_IN_ACTION_LINK, {
              target: "_blank",
              onClick: () => trackBrandPreviewFairesArrangementLinkClick(),
            }),
          }}
        />
      );
    case isInactiveBrandWithProducts:
      return (
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>Previewing your shop.</MediumFormatting> Retailers will see a unique product arrangement based on their store type and browsing behavior. <LinkFormatting>See it in action</LinkFormatting>"
          description={{
            text: "Brand preview banner for inactive brands with products",
            img: "https://cdn.faire.com/fastly/ad2673d5f4c0b4e2a3d1d1729a673b6a9a87786201b77a74522d5667681317d1.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            LinkFormatting: LinkFormatting(SEE_IT_IN_ACTION_LINK, {
              target: "_blank",
              onClick: () => trackBrandPreviewFairesArrangementLinkClick(),
            }),
          }}
        />
      );
    case isInactiveBrand:
      return (
        <StrictLocalMsg
          defaultMessage="<MediumFormatting>Previewing your shop.</MediumFormatting> When you’re ready, you can activate this page from your portal. <LinkFormatting>Go to portal</LinkFormatting>"
          description={{
            text: "Brand preview banner for inactive brands with no products",
            img: "https://cdn.faire.com/fastly/d91650d0188a5f5b809cb700c7265c5216fa02c8aec087c54157e80a90b9ab2a.png",
          }}
          values={{
            MediumFormatting: MediumFormatting,
            LinkFormatting: LinkFormatting(brandPortal(), {
              target: "_blank",
              onClick: () => trackBrandPreviewReturnToBrandPortalLinkClick(),
            }),
          }}
        />
      );
    default:
      return null;
  }
};
